import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    MilestoneConnection,
    MilestoneLoadState,
    MilestoneConnectionFormState,
    MilestoneConnectionID,
    MilestoneConnectionHeader,
    MilestoneConnectionDefault,
    MilestoneCamera,
    MilestoneCameraList,
    MilestoneCameraSyncRequest,
    DEFAULT_MILESTONE_BUFFER_MS,
} from "userful-chronos-app-common-js/dist/models/integration/milestone";
import {
    requestListConnections,
    requestAddConnection,
    requestUpdateConnection,
    requestRemoveConnection,
    requestSyncConnection,
    requestListCameras,
    requestSyncSelectedCameras,
} from "./msgs/milestoneMsgSender";

const normalizeConnection = (connection: MilestoneConnection): MilestoneConnection => {
    const onvifTls = connection.onvifTls ?? false;
    const rtspTls = connection.rtspTls ?? false;
    const useDigest = connection.useDigest ?? true;
    const adjustTime = connection.adjustTime ?? false;
    const bufferMs = connection.bufferMs ?? DEFAULT_MILESTONE_BUFFER_MS;
    const streamPath = connection.streamPath ?? "live";

    return {
        ...MilestoneConnectionDefault(),
        ...connection,
        onvifTls,
        rtspTls,
        useDigest,
        adjustTime,
        bufferMs,
        streamPath,
    };
};

type MilestoneCameraLoadState = "IDLE" | "LOADING" | "SUCCESS" | "ERROR";

const initialState: {
    connections: MilestoneConnection[];
    loadState: MilestoneLoadState;
    connection: MilestoneConnection;
    copyConnection: MilestoneConnection;
    formType: MilestoneConnectionFormState;
    openForm: boolean;
    camerasByConnection: Record<string, MilestoneCamera[]>;
    camerasLoadState: MilestoneCameraLoadState;
    camerasError: string | null;
    activeCameraConnectionId: string | null;
    cameraSyncState: MilestoneLoadState;
} = {
    connections: [],
    loadState: "RENDER",
    connection: MilestoneConnectionDefault(),
    copyConnection: MilestoneConnectionDefault(),
    formType: "Create",
    openForm: false,
    camerasByConnection: {},
    camerasLoadState: "IDLE",
    camerasError: null,
    activeCameraConnectionId: null,
    cameraSyncState: "RENDER",
};

export const milestoneSlice = createSlice({
    name: "milestoneSlice",
    initialState,
    reducers: {
        setConnections: (state, action: PayloadAction<MilestoneConnection[]>) => {
            state.connections = action.payload.map(normalizeConnection);
        },
        addOrUpdateConnection: (state, action: PayloadAction<MilestoneConnection>) => {
            const normalized = normalizeConnection(action.payload);
            const index = state.connections.findIndex((item) => item.id.value === normalized.id.value);
            if (index < 0) {
                state.connections = [...state.connections, normalized];
            } else {
                state.connections = [
                    ...state.connections.slice(0, index),
                    normalized,
                    ...state.connections.slice(index + 1),
                ];
            }
        },
        removeConnection: (state, action: PayloadAction<MilestoneConnectionID>) => {
            state.connections = state.connections.filter((item) => item.id.value !== action.payload.value);
        },
        getConnectionsFromServer: (state) => {
            requestListConnections();
        },
        addConnectionToServer: (state, action: PayloadAction<MilestoneConnectionHeader>) => {
            requestAddConnection(action.payload);
        },
        updateConnectionToServer: (state, action: PayloadAction<MilestoneConnectionHeader>) => {
            requestUpdateConnection(action.payload);
        },
        removeConnectionToServer: (state, action: PayloadAction<MilestoneConnectionID>) => {
            requestRemoveConnection(action.payload);
        },
        syncConnectionToServer: (state, action: PayloadAction<MilestoneConnectionID>) => {
            requestSyncConnection(action.payload);
        },
        changeLoadState: (state, action: PayloadAction<MilestoneLoadState>) => {
            state.loadState = action.payload;
        },
        setConnection: (state, action: PayloadAction<MilestoneConnection>) => {
            state.connection = normalizeConnection(action.payload);
        },
        setCopyConnection: (state, action: PayloadAction<MilestoneConnection>) => {
            state.copyConnection = normalizeConnection(action.payload);
        },
        setFormType: (state, action: PayloadAction<MilestoneConnectionFormState>) => {
            state.formType = action.payload;
        },
        setOpenForm: (state, action: PayloadAction<boolean>) => {
            state.openForm = action.payload;
        },
        requestCamerasFromServer: (state, action: PayloadAction<MilestoneConnectionID>) => {
            state.camerasLoadState = "LOADING";
            state.camerasError = null;
            state.activeCameraConnectionId = action.payload?.value ?? null;
            if (state.activeCameraConnectionId) {
                state.camerasByConnection = {
                    ...state.camerasByConnection,
                    [state.activeCameraConnectionId]: state.camerasByConnection[state.activeCameraConnectionId] ?? [],
                };
            }
            requestListCameras(action.payload);
        },
        setCameras: (state, action: PayloadAction<MilestoneCameraList>) => {
            const connectionId = action.payload.connectionId?.value;
            if (connectionId) {
                state.camerasByConnection = {
                    ...state.camerasByConnection,
                    [connectionId]: action.payload.cameras,
                };
                state.activeCameraConnectionId = connectionId;
            }
            state.camerasLoadState = "SUCCESS";
            state.camerasError = null;
        },
        setCamerasLoadState: (state, action: PayloadAction<{ state: MilestoneCameraLoadState; error?: string }>) => {
            state.camerasLoadState = action.payload.state;
            state.camerasError = action.payload.error ?? null;
        },
        syncSelectedCamerasToServer: (state, action: PayloadAction<MilestoneCameraSyncRequest>) => {
            state.cameraSyncState = "SAVING";
            requestSyncSelectedCameras(action.payload);
        },
        setCameraSyncState: (state, action: PayloadAction<MilestoneLoadState>) => {
            state.cameraSyncState = action.payload;
        },
    },
});

export const milestoneActions = milestoneSlice.actions;

export default milestoneSlice.reducer;
